import { BlueprintData } from '@rta/rta-blueprint-library';
import { useCallback, useContext } from 'react';

import { AppContext } from '../app-context';
import { ActionTypes as DesignActionTypes } from '../customer-designs/action-types';
import { DESIGN_PAGE_SIZE } from '../../config';
import { hydrateBlueprint } from './hydrate-blueprint';
import { readAutosaveEntries } from './read-autosave-entries';
import { selectNavigation, useAppSelector } from '@state';
import { useKeystoneSession } from '../../auth/use-keystone-session';

export type LoadMoreDesigns = {
	/** Designs the customer has that are not loaded yet. */
	remaining: number;
	hasMore: boolean;
	isLoadingMore: boolean;
	loadMore: () => Promise<void>;
};

/**
 * Pulls the next page of the customer's designs into app state.
 * Designs load a page at a time so a customer with dozens of them opens as fast as one with three.
 * @returns how many designs are still unloaded and the action that loads the next page of them
 */
export const useLoadMoreDesigns = (): LoadMoreDesigns => {
	const { appState: { customerDesigns }, appDispatch: dispatch } = useContext(AppContext);
	const { getToken, user } = useKeystoneSession();
	const appNav = useAppSelector(selectNavigation);

	const { designs, email, page, pageCount, totalCount, isLoadingMore } = customerDesigns;
	const remaining = Math.max(0, totalCount - designs.length);
	const hasMore = page > 0 && page < pageCount;

	const loadMore = useCallback(async (): Promise<void> => {
		if (!email || isLoadingMore || page >= pageCount) return;

		dispatch({ type: DesignActionTypes.loadMoreStart, payload: null });

		try {
			const { getCustomerBlueprints } = await import('@external');
			const [paged] = await getCustomerBlueprints({ email, page: page + 1, limit: DESIGN_PAGE_SIZE, getToken });

			if (!paged) return;

			dispatch({
				type: DesignActionTypes.setDesignPage,
				payload: { page: paged.page, pageCount: paged.pageCount, totalCount: paged.totalCount, index: paged.index ?? [] }
			});

			const autosaveEntries = readAutosaveEntries(email);
			const useDraft = (appNav.draft ?? false) || appNav.designVersion === null;

			(paged.data ?? []).filter(Boolean).forEach((blueprintData: BlueprintData, entryIndex: number) => {
				try {
					const hydrated = hydrateBlueprint({
						blueprintData,
						entryIndex,
						userEmail: user?.email,
						autosaveEntries,
						forceDraft: appNav.draft === true
					});

					if (!hydrated) return;

					const { blueprint, isDirty } = hydrated;
					const active = blueprint.kitchens.getActiveVersion();
					const version = useDraft ? (active?.baseVersion ?? null) : (active?.version ?? null);

					dispatch({ type: DesignActionTypes.addBlueprint, payload: { blueprint, version, useDraft, isDirty }});
				} catch (e) {
					console.error('[Blueprint Load More] failed to add a design', e);
				}
			});
		} finally {
			dispatch({ type: DesignActionTypes.loadMoreEnd, payload: null });
		}
	}, [email, page, pageCount, isLoadingMore, getToken, user?.email, appNav.draft, appNav.designVersion, dispatch]);

	return { remaining, hasMore, isLoadingMore, loadMore };
};
